import { useEffect, useMemo, useState } from 'react'

import { cn } from '@modules/lib/lib'
import { Input } from '@modules/shadcn/ui/Input/Input'

import {
  extractMaterialManufacturers,
  filterMaterials,
  type MaterialOption,
  type MaterialTargetPart,
} from './materialOptions'

interface MaterialPaletteProps {
  /** API から取得した素材一覧（屋根・外壁の両方が含まれる）. */
  materials: MaterialOption[]
  /** 現在フォーカス中のベースエリアに対応する部位. wall|roof のみ素材選択可能. */
  targetPart: MaterialTargetPart
  /** 選択中の素材ID（未選択は undefined）. */
  selectedMaterialId?: bigint
  /** 素材選択ハンドラ. */
  onMaterialSelect: (materialId: bigint) => void
}

interface SelectableContainerProps {
  selected: boolean
  onClick: () => void
  children: React.ReactNode
}

const SelectableContainer = ({ selected, onClick, children }: SelectableContainerProps) => (
  <button
    aria-pressed={selected}
    className={cn(
      'flex shrink-0 items-center gap-3 rounded-lg border bg-white px-4 py-3 transition-colors',
      selected ? 'border-2 border-emerald-500 bg-[#F4F4F5]' : 'border-[#E4E4E7]',
      'hover:bg-[#F4F4F5]',
    )}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
)

/**
 * 品番を表示してよいか判定する.
 *
 * 品番がブランド名と同一のマスタデータ（例: DIPLOMAT STAR/II）が実在するため、
 * そのまま出すとカード上段のブランド表示と重複してしまう。空文字の場合も含めて弾く.
 */
const canShowProductCode = (opt: MaterialOption): boolean =>
  opt.productCode !== '' && opt.productCode !== opt.brand

/** サムネイルの代替テキスト（ブランド・品番・カラー名の並び）. */
const buildMaterialImageAlt = (opt: MaterialOption): string =>
  canShowProductCode(opt)
    ? `${opt.brand} ${opt.productCode} ${opt.colorName}`
    : `${opt.brand} ${opt.colorName}`

/**
 * 素材変更パレット.
 *
 * 1st リリース要件 (Issue #1785):
 * - 屋根・外壁ベースエリアのみ対応 (`targetPart` で絞り込み)
 * - メーカーで絞り込んで素材カードグリッドから選択
 *
 * UI は ColorPalette.tsx と同じ枠組みを踏襲し、ColorSelectDialog のタブで切替する想定.
 */
export const MaterialPalette = ({
  materials,
  targetPart,
  selectedMaterialId,
  onMaterialSelect,
}: MaterialPaletteProps) => {
  // targetPart で先に絞り込んでからメーカー一覧を抽出する
  const partMaterials = useMemo(
    () => materials.filter(m => m.targetPart === targetPart),
    [materials, targetPart],
  )
  const totalCount = partMaterials.length

  const makers = useMemo(() => extractMaterialManufacturers(partMaterials), [partMaterials])

  const [selectedMaker, setSelectedMaker] = useState<string | null>(() => makers[0] ?? null)
  const [searchTerm, setSearchTerm] = useState<string>('')

  useEffect(() => {
    if (makers.length === 0) {
      setSelectedMaker(null)
      return
    }
    if (!selectedMaker || !makers.includes(selectedMaker)) {
      setSelectedMaker(makers[0]!)
    }
  }, [makers, selectedMaker])

  const filtered = useMemo(
    () => filterMaterials(partMaterials, selectedMaker, targetPart, searchTerm),
    [partMaterials, selectedMaker, targetPart, searchTerm],
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-x-hidden overflow-y-auto pb-4 pr-1">
      {/* メーカーを指定 */}
      <section className="shrink-0 space-y-2">
        <h3 className="text-xs font-medium text-[#18181B]">メーカーを指定</h3>
        <div className="-mx-4 overflow-x-auto px-4 pb-1">
          <div className="flex w-max gap-2">
            {makers.map(maker => (
              <SelectableContainer
                key={maker}
                onClick={() => setSelectedMaker(maker)}
                selected={selectedMaker === maker}
              >
                <span className="text-base font-medium text-[#09090B]">{maker}</span>
              </SelectableContainer>
            ))}
          </div>
        </div>
      </section>

      {/* キーワードで絞り込む */}
      <div className="shrink-0 space-y-2">
        <label className="text-xs font-medium text-[#18181B]" htmlFor="material-search">
          キーワードで絞り込む
        </label>
        <Input
          className="h-9"
          id="material-search"
          onChange={e => setSearchTerm(e.target.value)}
          type="text"
          value={searchTerm}
        />
      </div>

      {/* 検索結果カウント */}
      <p className="shrink-0 text-sm font-medium text-[#71717A]">
        {filtered.length}素材が見つかりました（全{totalCount}素材中）
      </p>

      {/* 素材カードリスト（素材は横並びにせず1列） */}
      <div className="pb-1">
        <div className="flex flex-col gap-2">
          {filtered.map(opt => {
            const isSelected = selectedMaterialId === opt.id
            return (
              <button
                aria-pressed={isSelected}
                className={cn(
                  'flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
                  isSelected
                    ? 'border-2 border-emerald-500 bg-[#F4F4F5]'
                    : 'border-[#E4E4E7] bg-white',
                  'hover:bg-[#F4F4F5]',
                )}
                key={opt.id.toString()}
                onClick={() => onMaterialSelect(opt.id)}
                type="button"
              >
                <img
                  alt={buildMaterialImageAlt(opt)}
                  className="h-10 w-[60px] shrink-0 rounded object-cover"
                  src={opt.thumbnailUrl}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="break-words text-sm font-medium text-[#09090B]">
                    {opt.brand}
                  </span>
                  {canShowProductCode(opt) && (
                    <span className="break-words text-xs text-[#71717A]">{opt.productCode}</span>
                  )}
                  <span className="break-words text-xs text-[#71717A]">{opt.colorName}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
